import React, { useState, useEffect } from 'react'

export default function Settings() {
    const [settings, setSettings] = useState<Record<string, string>>({})
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

    useEffect(() => {
        fetch('/api/settings')
            .then(res => res.json())
            .then(data => {
                setSettings((data as Record<string, string>) || {})
                setLoading(false)
            })
            .catch(err => {
                console.error(err)
                setMessage({ type: 'error', text: 'Failed to load settings.' })
                setLoading(false)
            })
    }, [])

    const handleChange = (key: string, value: string) => {
        setSettings(prev => ({ ...prev, [key]: value }))
    }

    const handleSave = async () => {
        setSaving(true)
        setMessage(null)
        try {
            const res = await fetch('/api/settings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings),
            })
            if (!res.ok) throw new Error('Save failed')
            setMessage({ type: 'success', text: 'Settings saved successfully.' })
        } catch (e) {
            setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Error saving settings' })
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return <div className="text-center mt-lg text-secondary">Loading settings...</div>
    }

    return (
        <div className="card animate-fade-in" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 className="mb-lg">System Settings</h2>
            <p className="text-sm text-secondary mb-lg">
                Configure the required API credentials and environmental parameters.
                These values are stored securely in the local D1 database.
            </p>

            {message && (
                <div className={`badge badge-${message.type === 'success' ? 'success' : 'error'} w-full text-center p-sm mb-lg`} style={{ padding: '12px', borderRadius: '8px' }}>
                    {message.text}
                </div>
            )}

            <div className="grid gap-md">
                <div className="form-group">
                    <label htmlFor="gemini_key">Gemini API Key</label>
                    <input
                        type="text"
                        id="gemini_key"
                        placeholder="AIzaSy..."
                        value={settings['GEMINI_API_KEY'] || ''}
                        onChange={e => handleChange('GEMINI_API_KEY', e.target.value)}
                    />
                    <p className="text-xs text-muted mt-xs">Required for parsing newsletters and generating scripts.</p>
                </div>

                <div className="form-group">
                    <label htmlFor="google_tts_json">Google Cloud TTS Service Account (JSON)</label>
                    <textarea
                        id="google_tts_json"
                        placeholder='{ "type": "service_account", "project_id": "...", ... }'
                        rows={5}
                        value={settings['GOOGLE_SERVICE_ACCOUNT_JSON'] || ''}
                        onChange={e => handleChange('GOOGLE_SERVICE_ACCOUNT_JSON', e.target.value)}
                    />
                    <p className="text-xs text-muted mt-xs">Complete JSON key for a Google Cloud service account with Text-to-Speech API access.</p>
                </div>

                <div className="form-group">
                    <label htmlFor="notification_email">Notification Email</label>
                    <input
                        type="text"
                        id="notification_email"
                        placeholder="you@example.com"
                        value={settings['NOTIFICATION_EMAIL'] || ''}
                        onChange={e => handleChange('NOTIFICATION_EMAIL', e.target.value)}
                    />
                    <p className="text-xs text-muted mt-xs">Email address to receive completion notifications. Must be a verified destination in Cloudflare Email Routing.</p>
                </div>

                <div className="form-group">
                    <label htmlFor="r2_base_url">R2 Public Base URL</label>
                    <input
                        type="text"
                        id="r2_base_url"
                        placeholder="https://assets.yourdomain.com"
                        value={settings['R2_PUBLIC_BASE_URL'] || ''}
                        onChange={e => handleChange('R2_PUBLIC_BASE_URL', e.target.value)}
                    />
                    <p className="text-xs text-muted mt-xs">The custom domain attached to your R2 bucket. Do not use r2.dev URLs.</p>
                </div>
            </div>

            <div className="flex justify-end mt-lg pt-md" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ minWidth: '120px' }}>
                    {saving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
        </div>
    )
}
